/**
 * A FALA da IA no Modo de Voz — fila de síntese/reprodução + as duas fontes
 * de texto falado. Extraído de `motor-de-voz.js` (voz v3, 08/09), que
 * re-exporta tudo daqui pros consumidores (`send-message.vue`,
 * `view-chat.vue`) não mudarem de import.
 *
 * ## As duas fontes, e a ordem de precedência (V3, voz v3, 08/09)
 *
 * [feedback do owner, 08/09] "o que fala deve ser diferente do que vai
 * escrito" — o backend passou a gerar uma versão FALADA curta do turno,
 * separada do texto escrito, e emiti-la como UM evento SSE `fala {texto}`
 * (contrato do dev de backend `dev-voz-backend`, mesma sessão). A partir de
 * agora:
 *
 * - **Fonte primária: o evento `fala`.** `processarEventoFala(texto)` — o
 *   handler SSE (`chat-event-handlers.js` → `apiChat._onEventFala`) chama
 *   isto assim que o evento chega, e ele já enfileira a síntese sozinho.
 * - **Fallback: o texto ESCRITO, só a 1ª frase, sem markdown.**
 *   `processarDeltaDeFala(delta)` continua sendo chamado a cada `token`/
 *   `text-delta` (registrado incondicionalmente em `view-chat.vue.created()`
 *   via `apiChat.setOnTextoDelta`), mas agora só ACUMULA — não sintetiza mais
 *   por delta. Se o turno terminar (`finalizarFalaProgressiva`) sem nenhum
 *   evento `fala` ter chegado (conversa antiga, provider sem o campo, ou o
 *   backend ainda não publicou), a 1ª frase COMPLETA do texto acumulado
 *   entra na fila, passada por `removerMarkdownSimples` — sem isso a IA
 *   "leria" `#`/`**`/links em voz alta.
 *
 * `_geracaoDeFala` é o mesmo padrão de dedupe por identidade que
 * `_destino`/`_audioAtual` já usavam neste módulo antes da extração: cada
 * turno de voz incrementa, e qualquer síntese/finalize de um turno VELHO que
 * resolva depois de um interromper/novo turno é descartado comparando a
 * geração capturada no enfileiramento contra a atual.
 */
import { apiVoz } from '@modules/aawz-intelligence-v2/api/apiVoz'
import { CONTENT_TYPE_PCM } from './avatar-ao-vivo/protocolo'
import { motorDeVoz } from './estado-da-voz'
import {
  avancarSegmentacao,
  fecharBufferRestante,
  removerMarkdownSimples,
} from './segmentador-de-frases'

let _geracaoDeFala = 0
let _bufferDeSegmentacao = ''
let _aceitandoDeltas = false
// [spec-016 T004] A voz escolhida NESTE turno (US-1) — capturada uma vez em
// `iniciarFalaProgressiva`, e não relida do store a cada frase: a escolha
// não pode mudar NO MEIO de uma resposta que já começou a ser sintetizada.
// `''` (nenhuma escolhida) é o AC2 — `_enfileirarFrase` não manda `voice_id`.
let _vozDoTurno = ''
// [V3] `true` assim que o PRIMEIRO evento `fala` deste turno chega — decide
// se `finalizarFalaProgressiva` precisa do fallback do texto escrito.
let _recebeuEventoFala = false
// [V3] A 1ª frase COMPLETA detectada no texto escrito, capturada de passagem
// enquanto os deltas chegam — é o candidato do fallback, sem precisar
// re-segmentar o buffer inteiro no fim.
let _primeiraFraseDoTexto = null

// O áudio da resposta tocando agora (ou `null`).
let _audioAtual = null

// ★ O DESTINO DO ÁUDIO — a única costura do avatar ao vivo (spec-009).
//
// Por padrão a fala sai por um `<audio>` deste módulo. Quando a conversa do
// PRIMEIRO CONTATO está com o avatar ao vivo, ela sai pelo canal do provedor,
// que renderiza o rosto falando o MESMO áudio. Tudo o mais — segmentação por
// frase, ordem da fila, geração, re-arme do mic, interrupção — fica igual:
// trocar o destino não troca o motor.
//
// ⚠️ **O Modo de Voz (GPT-Live) NUNCA passa por aqui.** Lá quem fala é o
// provedor, pelo data channel; esta fila nem é armada. É isso que faz o
// avatar ser "só uma porta diferente" e não uma bifurcação do caminho da voz.
//
// Contrato do destino: `{falar(pcmBase64) -> Promise<void>, interromper(),
// pedePcm: boolean}`. `pedePcm` decide o FORMATO que pedimos ao servidor —
// o `<audio>` toca MP3, o provedor exige PCM 16-bit 24 kHz.
let _destinoDeAudio = null

/**
 * Liga o destino alternativo. Devolve o objeto registrado, que é o que
 * `esquecerDestinoDeAudio` exige de volta — mesma guarda de identidade dos
 * provedores do `apiChat`: na troca de tela o `created` do novo roda ANTES do
 * `beforeDestroy` do velho, e limpar sem conferir apagaria o registro que
 * acabou de ser feito.
 */
export const registrarDestinoDeAudio = (destino) => {
  _destinoDeAudio = destino || null
  return _destinoDeAudio
}

/** Desliga, se ainda for o mesmo. Ver a guarda de identidade acima. */
export const esquecerDestinoDeAudio = (destino) => {
  if (_destinoDeAudio && _destinoDeAudio === destino) _destinoDeAudio = null
}

/** O destino atual pede PCM em vez de MP3? */
const _destinoPedePcm = () =>
  Boolean(_destinoDeAudio && _destinoDeAudio.pedePcm)

// Fila ORDENADA de segmentos — cada item é `{geracao, promise}`, onde a
// promise resolve pro áudio sintetizado (ou `null` em falha). A ORDEM da
// fila é a ordem de fala; a SÍNTESE roda em paralelo (menor latência total),
// mas o consumidor só avança pro próximo item depois que o atual TOCOU.
let _filaDeFala = []
let _tocandoFila = false
// Identidade do consumidor VIVO da fila. `interromperFalaDaIa` incrementa
// pra que o consumidor parado num `await` (síntese ou áudio) morra calado ao
// acordar — sem isto ele zeraria `_tocandoFila` por cima de um consumidor
// novo (dois tirando da mesma fila) ou reabriria o mic enquanto o `iniciar()`
// de quem interrompeu ainda espera o `getUserMedia` (dois mics abertos).
let _consumidorDaFila = 0

/**
 * Toca UM áudio e resolve quando termina (`ended`/`error`/falha de `play()`)
 * ou é pausado — `interromperFalaDaIa` só faz `pause()`, e sem ouvir `pause`
 * a promise nunca resolvia, prendendo `_tocandoFila` em `true` pro resto da
 * sessão. No fim natural o browser dispara `pause` E `ended`; `encerrar` é
 * idempotente de propósito.
 */
const _tocarUmAudio = (audioBase64, contentType) => {
  // ★ O avatar ao vivo assumiu a fala: o áudio vai ao provedor, que o
  // sincroniza com o rosto. `estadoDaFala` é escrito do MESMO jeito nos dois
  // caminhos — é ele que o computed `avatarFalando` lê, e é ele que a guarda
  // do N4 observa (`transporte-por-turnos.js`). Escrever diferente aqui
  // quebraria o barge-in local sem nenhum erro aparecer.
  // ⚠️ **O discriminante é o `contentType`, não a existência do destino.** O
  // formato é escolhido quando a frase é SINTETIZADA, e o avatar pode ser
  // ligado ENTRE a síntese e a fala: as frases da boas-vindas já estão em MP3
  // nesse instante. Sem esta guarda, bytes de MP3 iriam ao `agent.speak` como
  // se fossem PCM — o avatar fala ruído e o provedor responde
  // `audio_buffer_appended` normalmente, sem um erro em lugar nenhum. Quem
  // diz a verdade são os bytes em mãos, nunca a intenção de quem pediu.
  if (_destinoDeAudio && contentType === CONTENT_TYPE_PCM) {
    const destino = _destinoDeAudio
    motorDeVoz.estadoDaFala = 'ia'
    return Promise.resolve(destino.falar(audioBase64))
      .catch(() => {
        // Falha do provedor não derruba o turno: a resposta já está na tela.
        // A fila segue para a próxima frase, como numa síntese que falhou.
      })
      .then(() => {
        if (motorDeVoz.estadoDaFala === 'ia') motorDeVoz.estadoDaFala = null
      })
  }
  return new Promise((resolve) => {
    motorDeVoz.estadoDaFala = 'ia'
    const tipo = contentType || 'audio/mpeg'
    const audio = new Audio(`data:${tipo};base64,${audioBase64}`)
    _audioAtual = audio
    const encerrar = () => {
      if (_audioAtual === audio) _audioAtual = null
      if (motorDeVoz.estadoDaFala === 'ia') motorDeVoz.estadoDaFala = null
      resolve()
    }
    audio.addEventListener('ended', encerrar)
    audio.addEventListener('error', encerrar)
    audio.addEventListener('pause', encerrar)
    audio.play().catch(encerrar)
  })
}

/**
 * Consome a fila em ordem, um item de cada vez. Quando esvazia E o turno já
 * terminou de mandar segmentos (`!_aceitandoDeltas`), reabre o mic sozinho —
 * mesma regra de re-arme que já existia pro caminho não-progressivo, só que
 * agora condicionada à fila estar REALMENTE vazia, não só ao fim de UM áudio.
 *
 * `aoFilaEsvaziar` é injetado por `motor-de-voz.js` (reabrir o mic) — este
 * módulo não conhece `iniciar()` (evita import circular entre os dois).
 */
let _aoFilaEsvaziar = null

/** Chamado por `motor-de-voz.js` uma vez, no boot do módulo. */
export const registrarReaberturaDeM = (callback) => {
  _aoFilaEsvaziar = typeof callback === 'function' ? callback : null
}

// [spec-019 T003] Mesmo racional de `_aoFilaEsvaziar` acima: `motor-de-voz.js`
// injeta o canal de aviso (o MESMO `_destino.avisarErroDeTranscricao` que já
// existe lá, via `_avisarFalhaDeVoz`) — este módulo não conhece
// `motor-de-voz.js` de propósito (import circular, ver o cabeçalho do arquivo).
let _aoFalharSintese = null

/** Chamado por `motor-de-voz.js` uma vez, no boot do módulo. */
export const registrarAvisoDeFalhaDeFala = (callback) => {
  _aoFalharSintese = typeof callback === 'function' ? callback : null
}

/**
 * [spec-019 T005, US-5] Leitura pro OBSERVADOR de silêncio de
 * `motor-de-voz.js`: "tem algo em voo" nesta fila — texto do turno ainda
 * chegando (`_aceitandoDeltas`, true do `iniciarFalaProgressiva` até o
 * `finalizarFalaProgressiva`, cobrindo a espera pela rede) OU a fila de
 * síntese/reprodução ainda processando (`_tocandoFila`, que cobre da
 * síntese até o áudio terminar de tocar — ver `_consumirFilaDeFala`).
 *
 * Getter, não a variável mutável (mesmo racional dos `registrar*` acima) —
 * mas aqui é CHAMADA direta, não callback registrado: `motor-de-voz.js` já
 * importa este módulo (é ele quem re-exporta `iniciarFalaProgressiva` etc.
 * pros consumidores), então não há import circular a evitar.
 */
export const algoEmVooNaFala = () => _aceitandoDeltas || _tocandoFila

// [spec-019 T003, ADR-019-04] `true` assim que o aviso de UMA falha de
// síntese já saiu neste turno — uma resposta longa vira N frases, e N
// falhas não podem virar N avisos empilhados escondendo o texto (que já
// está na tela) atrás deles. Zerado em `iniciarFalaProgressiva`.
let _avisoDeFalhaDeSinteseEnviado = false

/**
 * Uma síntese que falhou (erro, prazo, recusa) dispara UM aviso por TURNO
 * (US-3 AC1). `geracao` é a mesma trava por identidade que
 * `_consumirFilaDeFala` já usa (`item.geracao !== _geracaoDeFala`): uma
 * falha que resolve DEPOIS de um interromper/turno novo pertence a um turno
 * MORTO — interromper não é falhar (US-3, "cuidado" do brief), e avisar
 * aqui mentiria sobre qual resposta o consultor está ouvindo agora.
 */
const _avisarFalhaDeSinteseUmaVez = (geracao) => {
  if (geracao !== _geracaoDeFala) return
  if (_avisoDeFalhaDeSinteseEnviado) return
  _avisoDeFalhaDeSinteseEnviado = true
  if (_aoFalharSintese) _aoFalharSintese()
}

const _consumirFilaDeFala = async (consumidor) => {
  const item = _filaDeFala.shift()
  if (!item) {
    _tocandoFila = false
    if (!_aceitandoDeltas && motorDeVoz.sessaoDeVozAtiva && _aoFilaEsvaziar) {
      _aoFilaEsvaziar()
    }
    return
  }
  const resultado = await item.promise
  // Interrompido enquanto esperava: quem interrompeu já esvaziou a fila e
  // zerou `_tocandoFila` — este consumidor só sai, sem tocar nem reabrir.
  if (consumidor !== _consumidorDaFila) return
  // Um turno novo começou (geração mudou sem interromper, ex.: reenvio)
  // enquanto esta frase sintetizava — é frase do turno velho: descarta e
  // segue para a próxima, que pode já ser do turno novo.
  if (item.geracao !== _geracaoDeFala) return _consumirFilaDeFala(consumidor)
  if (resultado && resultado.audioBase64) {
    await _tocarUmAudio(resultado.audioBase64, resultado.contentType)
  }
  if (consumidor === _consumidorDaFila) _consumirFilaDeFala(consumidor)
}

const _tocarFilaSeOcioso = () => {
  if (_tocandoFila) return
  _tocandoFila = true
  _consumidorDaFila += 1
  _consumirFilaDeFala(_consumidorDaFila)
}

/**
 * Pede a síntese de UMA frase e a põe na fila, na posição em que chegou.
 *
 * [spec-019 T003, D3] Falha (erro, prazo, recusa) devolve `null` — a fila
 * segue avançando (US-3 AC3) — mas, antes, avisa o consultor UMA vez por
 * turno (`_avisarFalhaDeSinteseUmaVez`): sem isto, a IA ficava muda em
 * silêncio, sem snack, sem log, sem sinal na orb.
 */
const _enfileirarFrase = (frase, geracao) => {
  // ★ O argumento `paraAvatar` só EXISTE quando o avatar está ligado — não é
  // `false` explícito. Assim o pedido sem avatar é BYTE A BYTE o de hoje, e o
  // invariante "nada muda sem o avatar" fica ESTRUTURAL em vez de prometido.
  //
  // ⚠️ Não é preciosismo: as suítes de voz comparam a chamada INTEIRA
  // (`toHaveBeenCalledWith`), e três delas ficaram vermelhas com o `false`
  // explícito. Elas estavam certas — a chamada tinha mudado.
  const argumentos = { texto: frase, voice_id: _vozDoTurno || undefined }
  if (_destinoPedePcm()) argumentos.paraAvatar = true

  const promise = apiVoz
    .sintetizar(argumentos)
    .then((r) => {
      if (!r || r.status !== 200 || !r.value) {
        _avisarFalhaDeSinteseUmaVez(geracao)
        return null
      }
      return { audioBase64: r.value.audio_base64, contentType: r.value.content_type }
    })
    .catch(() => {
      _avisarFalhaDeSinteseUmaVez(geracao)
      return null
    })
  _filaDeFala.push({ geracao, promise })
  _tocarFilaSeOcioso()
}

/**
 * Início de um turno de fala — chamado pelo `view-chat.vue` ANTES de mandar
 * a mensagem por voz. Devolve a GERAÇÃO deste turno; guarde-a e repasse pra
 * `finalizarFalaProgressiva(geracao)` no fim.
 *
 * @param {string} [vozId] Voz escolhida NESTA conversa (spec-016 T004,
 *   US-1) — capturada aqui e usada por TODAS as frases deste turno. `''`/
 *   ausente = nenhuma escolhida (US-1 AC2): `apiVoz.sintetizar` não leva
 *   `voice_id`, byte a byte o comportamento de hoje.
 * @returns {number}
 */
export const iniciarFalaProgressiva = (vozId) => {
  _geracaoDeFala += 1
  _bufferDeSegmentacao = ''
  _aceitandoDeltas = true
  _recebeuEventoFala = false
  _primeiraFraseDoTexto = null
  _vozDoTurno = vozId || ''
  // [spec-019 T003, ADR-019-04] "Um aviso por turno" — zera aqui, não em
  // `interromperFalaDaIa` (a trava por `geracao` já cobre o turno morto).
  _avisoDeFalhaDeSinteseEnviado = false
  return _geracaoDeFala
}

/**
 * Chamado a cada delta de texto do turno (SSE `token`/`text-delta`). No-op
 * fora de um turno de fala ativo (`_aceitandoDeltas`) — é o que faz mensagem
 * DIGITADA não sintetizar nada, mesmo com o mesmo `apiChat` registrando o
 * hook incondicionalmente.
 *
 * [V3] NÃO sintetiza mais por delta — só acumula e guarda a 1ª frase
 * completa, candidata do FALLBACK (a fonte real é `processarEventoFala`).
 */
export const processarDeltaDeFala = (delta) => {
  if (!_aceitandoDeltas) return
  const resultado = avancarSegmentacao(_bufferDeSegmentacao, delta)
  _bufferDeSegmentacao = resultado.bufferPendente
  if (_primeiraFraseDoTexto === null && resultado.frases.length) {
    _primeiraFraseDoTexto = resultado.frases[0]
  }
}

/**
 * [V3] Chamado pelo handler do evento SSE `fala` (`apiChat._onEventFala`) —
 * a fonte PRIMÁRIA da fala do turno: uma frase curta, gerada pelo backend
 * especificamente para ser FALADA (diferente do texto escrito). No-op fora
 * de um turno de fala ativo, mesma guarda de `processarDeltaDeFala`.
 */
export const processarEventoFala = (texto) => {
  if (!_aceitandoDeltas) return
  const limpo = String(texto || '').trim()
  if (!limpo) return
  _recebeuEventoFala = true
  _enfileirarFrase(limpo, _geracaoDeFala)
}

/**
 * Fim do turno (a resposta terminou de chegar).
 *
 * `geracao` tem que bater com a atual: se o usuário já interrompeu (ou um
 * turno novo já começou) entre o início e o fim deste, este `finalize` é de
 * um turno MORTO e não deve reabrir nada nem falar nada dele.
 *
 * [V3] Se o evento `fala` já enfileirou a fala deste turno, não há mais
 * nada a fazer aqui além de garantir que a fila está tocando. Só cai no
 * FALLBACK (1ª frase do texto escrito, sem markdown) quando o turno terminou
 * SEM nenhum evento `fala`.
 *
 * @param {number} geracao a que `iniciarFalaProgressiva` devolveu
 */
export const finalizarFalaProgressiva = (geracao) => {
  if (geracao !== _geracaoDeFala || !_aceitandoDeltas) return
  _aceitandoDeltas = false
  if (_recebeuEventoFala) {
    _tocarFilaSeOcioso()
    return
  }
  const restantes = fecharBufferRestante(_bufferDeSegmentacao)
  const candidata = _primeiraFraseDoTexto || (restantes.length ? restantes[0] : '')
  _bufferDeSegmentacao = ''
  const semMarkdown = removerMarkdownSimples(candidata)
  if (semMarkdown) _enfileirarFrase(semMarkdown, geracao)
  _tocarFilaSeOcioso()
}

/**
 * Cala o que estiver falando AGORA, nos dois destinos.
 *
 * ⚠️ O avatar precisa do barge-in DELE: pausar o nosso `<audio>` não apaga o
 * áudio que já está no buffer do provedor, e ele seguiria falando por
 * segundos depois de a pessoa ter interrompido.
 */
const _calarOQueEstaFalando = () => {
  if (_audioAtual) {
    const audio = _audioAtual
    _audioAtual = null
    audio.pause()
  }
  if (_destinoDeAudio && typeof _destinoDeAudio.interromper === 'function') {
    _destinoDeAudio.interromper()
  }
}

/**
 * Corta a fala da IA em andamento — usado sempre que o usuário decide falar
 * (clique manual ou barge-in). Também descarta a FILA inteira (não só o
 * áudio tocando agora) e para de aceitar deltas/eventos do turno
 * interrompido — falar por cima é "não quero mais ouvir o resto desta
 * resposta", não só "pule esta frase".
 */
export const interromperFalaDaIa = () => {
  _geracaoDeFala += 1
  _filaDeFala = []
  // A fila foi descartada: nada mais em voo, e o consumidor que estava no
  // meio dela é invalidado (ver `_consumidorDaFila`).
  _tocandoFila = false
  _consumidorDaFila += 1
  _aceitandoDeltas = false
  _recebeuEventoFala = false
  _bufferDeSegmentacao = ''
  _primeiraFraseDoTexto = null
  _calarOQueEstaFalando()
  if (motorDeVoz.estadoDaFala === 'ia') motorDeVoz.estadoDaFala = null
}

/**
 * Toca a resposta falada da IA DE UMA VEZ (texto inteiro, não progressivo).
 * ★ Fora do caminho principal desde a fala progressiva — `view-chat.vue` não
 * chama mais isto para o Modo de Voz. Mantido como a PRIMITIVA de
 * tocar-um-áudio-e-re-armar (`_tocarUmAudio`, reusada pela fila), com
 * cobertura de teste própria — caminho alternativo de tocar UM áudio só,
 * fora de um turno de fala progressiva (o bridge no composer foi apagado).
 */
export const reproduzirResposta = (audioBase64, contentType) => {
  if (!audioBase64) return
  const geracao = _geracaoDeFala
  _tocarUmAudio(audioBase64, contentType).then(() => {
    // Cortado por `interromperFalaDaIa` (o `pause` também resolve): quem
    // cortou já está abrindo o mic — reabrir daqui seria um 2º `iniciar()`.
    if (geracao !== _geracaoDeFala) return
    if (motorDeVoz.sessaoDeVozAtiva && _aoFilaEsvaziar) {
      _aoFilaEsvaziar()
    }
  })
}
